import React, { useMemo, useState, useRef } from 'react'
import styled from '@emotion/styled'
import { ActionButton } from '../commons/ActionButton'
import Refresh from '@mui/icons-material/Refresh'
import Delete from '@mui/icons-material/Delete'
import { ReactComponent as CSVIcon } from '../../assets/images/CSV.svg'
import { setRecoil } from '../../state/recoilNexus'
import { cacheBuster } from '../../state/state'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt'
import Client from '../../cms/client'
import { useRecoilState, useRecoilValue, useSetRecoilState, useRecoilValueLoadable } from 'recoil'
import {
	bulkTableState,
	bulkTableFilterAtom,
	BulkUploadStatus,
	selectedRows,
	hasUploadedFileToggle,
	bulkTableColumnsState,
	bulkUploadFilesSelector
} from './state'
import { BulkFileStore } from './service'
import { useSnackbar } from 'notistack'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import { Messages } from '../../utils/messages'
import { modalAtom, ModalWrapperProps } from '../ModalDialogs/ModalWrapper'
import { confirmationModalAtom } from '../ModalDialogs/ConfirmationModal'
import MoveModal from '../ModalDialogs/MoveModal'
import { UUID } from '../../cms/types'
import Divider from '@mui/material/Divider'
import { Objects } from '../../utils/objects'
import BackupIcon from '@mui/icons-material/Backup'
import { downloadAsCsv, fi } from '../../utils/helpers'

const Wrapper = styled.div`
	padding: 16px;
	display: flex;
	justify-content: space-between;

	hr {
		margin: 6px 8px;
		height: 20px;
	}

	label {
		line-height: 1;
		cursor: pointer;
	}
`

const StatusWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;

	dl {
		display: flex;
		gap: 4px;

		dt {
			text-transform: capitalize;
		}

		dd {
			font-weight: bold;
		}
	}

	.successful {
		color: var(--color-green);
	}

	.errors {
		color: var(--color-red);
	}
`

const ActionWrapper = styled.div`
	display: flex;

	hr {
		margin: 6px 8px;
		height: 20px;
	}

	label {
		line-height: 1;
		cursor: pointer;
	}
`

const BulkActions = ({ onUpload }: { onUpload: (list: FileList) => void }) => {
	const [selectedIds, setSelectedId] = useRecoilState(selectedRows)
	const [canMove, setCanMove] = useState(false)
	const { enqueueSnackbar } = useSnackbar()
	const setModalMove = useSetRecoilState(modalAtom)
	const [state, setState] = useRecoilState(bulkTableState)
	const bulkFileAll = useRecoilValue(cacheBuster(`bulk-file-all`))
	const tableState = useRecoilValue(bulkTableState)
	const filters = useRecoilValue(bulkTableFilterAtom)
	const fields = useRecoilValue(bulkTableColumnsState)
	const hasUploadedFile = useRecoilValue(hasUploadedFileToggle)
	const bulkFilesLoadable = useRecoilValueLoadable(bulkUploadFilesSelector)
	const isRefreshing = bulkFilesLoadable.state === 'loading'
	const fileInputRef = useRef<HTMLInputElement>(null)

	const bulkUploadStatus = useMemo<BulkUploadStatus>(() => {
		return BulkFileStore.uploadStatus()
	}, [bulkFileAll, tableState, hasUploadedFile])

	const isUploading = bulkUploadStatus.isUploading

	const totalItems = useMemo(() => {
		return BulkFileStore.getAll().length
	}, [bulkFileAll])

	const selectedItems = useMemo(() => {
		const selected = BulkFileStore.filteredFiles(filters).filter((f) =>
			selectedIds.includes(f.bulkid)
		)
		const withError = Boolean(selected.find((f) => !Objects.isEmpty(f.errors)))
		setCanMove(selected.length > 0 && !withError)
		return selected
	}, [selectedIds])

	const onDelete = () => {
		if (selectedIds.length === 0) return
		setRecoil(confirmationModalAtom, {
			message: (
				<>
					Are you sure you want to delete <strong>{selectedIds.length}</strong>{' '}
					selected {selectedIds.length === 1 ? 'file' : 'files'}?
				</>
			),
			onConfirm: () => {
				setState((val) => ({ ...val, selectedCell: [] }))
				const bulkFileIds = BulkFileStore.filteredFiles(filters).map(
					(f) => f.bulkid
				)
				return Client.deleteBulkFiles(
					selectedIds.filter((id) => bulkFileIds.includes(id))
				)
					.then(() => {
						enqueueSnackbar('Files removed', { variant: 'success' })
						BulkFileStore.removeByIds(selectedIds)
						setSelectedId([])
					})
					.catch(() => {
						enqueueSnackbar('Error deleting files', { variant: 'error' })
					})
					.finally(() => {
						setRecoil(cacheBuster(`bulk-file-all`), (val) => val + 1)
					})
			}
		})
	}

	const onMoveSelected = () => {
		setModalMove({
			component: (props: ModalWrapperProps) => {
				return (
					<MoveModal
						{...props}
						onConfirm={async (targetId: UUID) => {
							setState((val) => ({ ...val, selectedCell: [] }))

							const ids = selectedItems.map((item) => item.bulkid)
							const result = await Client.moveBulkItems(ids, targetId)
							const created = result?.created ?? ids

							BulkFileStore.removeByIds(created)
							setSelectedId([])
							setRecoil(cacheBuster('bulkFilesList'), (val) => val + 1)

							if (result?.failed?.length > 0) {
								enqueueSnackbar(
									`${created.length} items moved, ${result.failed.length} failed`,
									{ variant: 'warning' }
								)
							} else {
								enqueueSnackbar(`${created.length} items created and moved`, {
									variant: 'success'
								})
							}
						}}
					/>
				)
			}
		})
	}

	const uploadFiles = (evt) => {
		onUpload(evt.target.files)
		evt.target.value = ''
	}

	const downloadReport = () => {
		const reportData = BulkFileStore.generateReport(
			BulkFileStore.filteredFiles(filters),
			fields
		)

		downloadAsCsv(reportData)
	}

	return (
		<Wrapper>
			<StatusWrapper>
				{fi(
					bulkUploadStatus.total,
					Object.entries(bulkUploadStatus).map(([key, value]) => {
						return (
							<dl className={key}>
								<dt>{fi(key === 'errors', 'failed with errors', key)}</dt>
								<dd>{value}</dd>
							</dl>
						)
					})
				)}
			</StatusWrapper>
			<ActionWrapper>
				<input
					ref={fileInputRef}
					style={{ display: 'none' }}
					multiple={true}
					type="file"
					data-testid={'bulk-upload-file-picker'}
					accept=".doc,.docx,.pdf,.csv,.zip,.ppt,.pptx,.xls,.xlsx,.xlsm,.mp3,.mp4,.wav"
					onChange={uploadFiles}
				/>
				<ActionButton
					data-testid="table-upload-files-action-button"
					title="Upload files"
					disabled={isRefreshing || isUploading}
					onClick={() => fileInputRef.current?.click()}
				>
						<BackupIcon />
				</ActionButton>

				<ActionButton
					data-testid="table-move-items-action-button"
					disabled={!canMove}
					title={Messages.MoveItems}
					onClick={onMoveSelected}
				>
					<DriveFileMoveOutlinedIcon />
				</ActionButton>

				<Divider orientation="vertical" variant="middle" flexItem />

				<ActionButton
					data-testid="bulk-download-template-button"
					onClick={() => {
						Client.excelTemplate().catch()
					}}
					disabled={false}
					title="Download Excel template"
				>
					<SystemUpdateAltIcon />
				</ActionButton>

				<Divider orientation="vertical" variant="middle" flexItem />

				<ActionButton
					data-testid="bulk-download-report"
					onClick={downloadReport}
					title={'Export table results to CSV'}
					disabled={totalItems === 0}
				>
					<CSVIcon title="Export table results to CSV" />
				</ActionButton>
				<ActionButton
					data-testid="bulk-refresh-button"
					onClick={() => {
						setRecoil(cacheBuster('bulkFilesList'), (val) => val + 1)
					}}
					disabled={isRefreshing || isUploading}
					title={'Refresh'}
				>
					<Refresh />
				</ActionButton>

				<ActionButton
					data-testid="bulk-delete-selected-button"
					onClick={onDelete}
					disabled={selectedIds.length === 0}
					title="Delete selected files"
				>
					<Delete />
				</ActionButton>
			</ActionWrapper>
		</Wrapper>
	)
}

export default BulkActions
